/**
 * Administrator panel of the prompt-pattern catalog (TASK-0039).
 *
 * The panel is the only place a pattern is authored, and it renders the three things a pattern has to get right: its
 * name (unique among live patterns), the levels it applies to and its suggestion priority. The level applicability is a
 * set of checkboxes over the bit mask the API stores, so an administrator picks levels rather than bits, and the priority
 * is a number the deterministic suggestion policy orders by.
 *
 * Two rules of the lifecycle are rendered rather than hidden:
 *
 * - deleting is soft, so a draft or a saved prompt that named the pattern keeps resolving it and the generation path can
 *   refuse the selection by name;
 * - restoring a removed pattern brings it back **disabled**, so publishing it again is a deliberate second action.
 */

import { Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { LtrInput } from '@/components/ui/input';
import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui/surface';
import {
  useCreatePromptPattern,
  useDeletePromptPattern,
  usePromptPatternCatalog,
  useRestorePromptPattern,
  useSetPromptPatternState,
  useUpdatePromptPattern,
} from '@/features/wizard/hooks/usePromptPatterns';
import { useProblemMessage } from '@/lib/errors';
import {
  PROMPT_LEVEL_APPLICABILITY,
  PROMPT_LEVELS,
  type PromptLevel,
  type PromptPattern,
} from '@/types/api';

/** One draft row, held as strings so a half-typed field is never coerced behind the user's back. */
interface PatternDraft {
  name: string;
  description: string;
  instructions: string;
  /** Levels the pattern applies to, as `PromptLevel` names. */
  levels: PromptLevel[];
  /** Priority as text, because an empty field must not read as zero while the user is typing. */
  suggestionPriority: string;
  isActive: boolean;
}

/** The empty draft a create form starts from. */
function emptyDraft(): PatternDraft {
  return {
    name: '',
    description: '',
    instructions: '',
    levels: ['Standard', 'Expert'],
    suggestionPriority: '100',
    isActive: true,
  };
}

/** Turns a set of level names into the bit mask the API stores. */
function toMask(levels: readonly PromptLevel[]): number {
  return levels.reduce((mask, level) => mask | PROMPT_LEVEL_APPLICABILITY[level], 0);
}

/** Reads the mask back into the level names the checkboxes render. */
function toLevels(mask: number): PromptLevel[] {
  return PROMPT_LEVELS.filter((level) => (mask & PROMPT_LEVEL_APPLICABILITY[level]) !== 0);
}

/** Projects a stored pattern onto an editable draft. */
function draftOf(pattern: PromptPattern): PatternDraft {
  return {
    name: pattern.name,
    description: pattern.description ?? '',
    instructions: pattern.instructions,
    levels: toLevels(pattern.levels),
    suggestionPriority: String(pattern.suggestionPriority),
    isActive: pattern.isActive,
  };
}

/** Renders the catalog list, the create form and the lifecycle actions. */
export function PromptPatternAdmin() {
  const { t } = useTranslation();
  const messageFor = useProblemMessage();
  const catalog = usePromptPatternCatalog(true, true);
  const create = useCreatePromptPattern();
  const update = useUpdatePromptPattern();
  const setState = useSetPromptPatternState();
  const remove = useDeletePromptPattern();
  const restore = useRestorePromptPattern();

  const [draft, setDraft] = useState<PatternDraft>(emptyDraft);
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<PatternDraft>(emptyDraft);

  const rows = catalog.data ?? [];

  const submitCreate = () => {
    create.mutate(
      {
        name: draft.name.trim(),
        instructions: draft.instructions.trim(),
        levels: toMask(draft.levels),
        suggestionPriority: Number(draft.suggestionPriority) || 0,
        description: draft.description.trim().length === 0 ? null : draft.description.trim(),
        isActive: draft.isActive,
      },
      { onSuccess: () => setDraft(emptyDraft()) },
    );
  };

  const submitEdit = (patternId: string) => {
    update.mutate(
      {
        patternId,
        payload: {
          name: editDraft.name.trim(),
          instructions: editDraft.instructions.trim(),
          levels: toMask(editDraft.levels),
          suggestionPriority: Number(editDraft.suggestionPriority) || 0,
          description:
            editDraft.description.trim().length === 0 ? null : editDraft.description.trim(),
        },
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('promptPatterns.title')}</CardTitle>
        <CardDescription>{t('promptPatterns.subtitle')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {catalog.isPending ? (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        ) : null}

        {catalog.isError ? <Alert variant="destructive">{messageFor(catalog.error)}</Alert> : null}
        {create.isError ? <Alert variant="destructive">{messageFor(create.error)}</Alert> : null}
        {update.isError ? <Alert variant="destructive">{messageFor(update.error)}</Alert> : null}
        {setState.isError ? (
          <Alert variant="destructive">{messageFor(setState.error)}</Alert>
        ) : null}
        {remove.isError ? <Alert variant="destructive">{messageFor(remove.error)}</Alert> : null}
        {restore.isError ? <Alert variant="destructive">{messageFor(restore.error)}</Alert> : null}

        {rows.length === 0 && !catalog.isPending ? (
          <p className="text-sm text-muted-foreground">{t('promptPatterns.empty')}</p>
        ) : null}

        <ul className="flex flex-col gap-2">
          {rows.map((pattern) => (
            <li key={pattern.id} className="rounded-lg border border-border p-3">
              <PatternRow
                pattern={pattern}
                editing={editing === pattern.id}
                editDraft={editDraft}
                onEditDraftChange={(patch) => setEditDraft((current) => ({ ...current, ...patch }))}
                onStartEdit={() => {
                  setEditing(pattern.id);
                  setEditDraft(draftOf(pattern));
                }}
                onCancelEdit={() => setEditing(null)}
                onSaveEdit={() => submitEdit(pattern.id)}
                isSaving={update.isPending}
                onToggleState={() =>
                  setState.mutate({ patternId: pattern.id, isActive: !pattern.isActive })
                }
                isTogglingState={setState.isPending}
                onDelete={() => remove.mutate(pattern.id)}
                isDeleting={remove.isPending}
                onRestore={() => restore.mutate(pattern.id)}
                isRestoring={restore.isPending}
              />
            </li>
          ))}
        </ul>

        <div className="rounded-lg border border-dashed border-border p-3">
          <p className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Plus aria-hidden className="h-4 w-4" />
            {t('promptPatterns.createTitle')}
          </p>

          <PatternFields
            draft={draft}
            onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
          />

          <div className="mt-3">
            <Button
              type="button"
              size="sm"
              disabled={
                create.isPending ||
                draft.name.trim().length === 0 ||
                draft.instructions.trim().length === 0 ||
                draft.levels.length === 0
              }
              onClick={submitCreate}
            >
              <Plus aria-hidden className="h-4 w-4" />
              {t('promptPatterns.create')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Props accepted by {@link PatternRow}. */
interface PatternRowProps {
  /** Stored pattern the row renders. */
  pattern: PromptPattern;
  /** Whether the row is in edit mode. */
  editing: boolean;
  /** Draft of the edit, when the row is in edit mode. */
  editDraft: PatternDraft;
  /** Applies a field change to the edit draft. */
  onEditDraftChange: (patch: Partial<PatternDraft>) => void;
  /** Enters edit mode. */
  onStartEdit: () => void;
  /** Leaves edit mode without saving. */
  onCancelEdit: () => void;
  /** Saves the edit. */
  onSaveEdit: () => void;
  /** Whether the edit is in flight. */
  isSaving: boolean;
  /** Enables or disables the pattern. */
  onToggleState: () => void;
  /** Whether the state change is in flight. */
  isTogglingState: boolean;
  /** Soft-deletes the pattern. */
  onDelete: () => void;
  /** Whether the deletion is in flight. */
  isDeleting: boolean;
  /** Restores the pattern. */
  onRestore: () => void;
  /** Whether the restore is in flight. */
  isRestoring: boolean;
}

/** One catalog row: its metadata and the lifecycle actions that apply to it. */
function PatternRow({
  pattern,
  editing,
  editDraft,
  onEditDraftChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  isSaving,
  onToggleState,
  isTogglingState,
  onDelete,
  isDeleting,
  onRestore,
  isRestoring,
}: PatternRowProps) {
  const { t } = useTranslation();

  return (
    <>
      {editing ? (
        <PatternFields draft={editDraft} onChange={onEditDraftChange} />
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{pattern.name}</span>
            <Badge variant={pattern.isActive ? 'default' : 'outline'}>
              {pattern.isActive ? t('promptPatterns.active') : t('promptPatterns.inactive')}
            </Badge>
            {pattern.isDeleted ? (
              <Badge variant="secondary">{t('promptPatterns.deleted')}</Badge>
            ) : null}
            <Badge variant="outline">
              {t('promptPatterns.priority', { value: pattern.suggestionPriority })}
            </Badge>
            {toLevels(pattern.levels).map((level) => (
              <Badge key={level} variant="outline">
                {t(`level.${level}`)}
              </Badge>
            ))}
          </div>

          {pattern.description === null ? null : (
            <p className="text-xs text-muted-foreground">{pattern.description}</p>
          )}

          <p dir="ltr" className="whitespace-pre-wrap text-xs text-muted-foreground">
            {pattern.instructions}
          </p>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {editing ? (
          <>
            <Button type="button" size="sm" onClick={onSaveEdit} disabled={isSaving}>
              <Save aria-hidden className="h-4 w-4" />
              {t('common.save')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onCancelEdit}>
              {t('common.cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button type="button" size="sm" variant="outline" onClick={onStartEdit}>
              {t('common.edit')}
            </Button>

            {pattern.isDeleted ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isRestoring}
                onClick={onRestore}
              >
                <RotateCcw aria-hidden className="h-4 w-4" />
                {t('promptPatterns.restore')}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isTogglingState}
                  onClick={onToggleState}
                >
                  {pattern.isActive ? t('promptPatterns.disable') : t('promptPatterns.enable')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={isDeleting}
                  onClick={onDelete}
                >
                  <Trash2 aria-hidden className="h-4 w-4" />
                  {t('promptPatterns.delete')}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** Props accepted by {@link PatternFields}. */
interface PatternFieldsProps {
  /** Draft being edited. */
  draft: PatternDraft;
  /** Applies a field change. */
  onChange: (patch: Partial<PatternDraft>) => void;
}

/** The fields every pattern write carries. */
function PatternFields({ draft, onChange }: PatternFieldsProps) {
  const { t } = useTranslation();

  const toggleLevel = (level: PromptLevel) => {
    onChange({
      levels: draft.levels.includes(level)
        ? draft.levels.filter((candidate) => candidate !== level)
        : [...draft.levels, level],
    });
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('promptPatterns.name')}</span>
        <input
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={draft.name}
          onChange={(event) => onChange({ name: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('promptPatterns.priorityLabel')}</span>
        <LtrInput
          inputMode="numeric"
          value={draft.suggestionPriority}
          onChange={(event) => onChange({ suggestionPriority: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        <span className="text-xs text-muted-foreground">{t('promptPatterns.description')}</span>
        <input
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        <span className="text-xs text-muted-foreground">{t('promptPatterns.instructions')}</span>
        <textarea
          rows={4}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={draft.instructions}
          onChange={(event) => onChange({ instructions: event.target.value })}
        />
      </label>

      <fieldset className="flex flex-col gap-2 sm:col-span-2">
        <legend className="text-xs text-muted-foreground">{t('promptPatterns.levels')}</legend>
        <div className="flex flex-wrap gap-4">
          {PROMPT_LEVELS.map((level) => (
            <label key={level} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.levels.includes(level)}
                onChange={() => toggleLevel(level)}
              />
              {t(`level.${level}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <input
          type="checkbox"
          checked={draft.isActive}
          onChange={(event) => onChange({ isActive: event.target.checked })}
        />
        {t('promptPatterns.activeLabel')}
      </label>
    </div>
  );
}
